import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { ClientId } from "../../domain/types";
import type { ZohoConnectStateDTO } from "../../data/zohoConnectApi";
import {
  advanceZohoConnect,
  retryZohoConnect,
  retryZohoReadback,
  startZohoConnect,
  zohoConnectStatus,
} from "../../data/zohoConnectApi";
import { dbTrue } from "../../data/zohoRecordsApi";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { Callout } from "../../components/Callout";
import { ConnectionSequence } from "../../components/ConnectionSequence";
import { SubStepShell } from "../../components/SubStepShell";
import { CO_BASE } from "../../domain/steps";
import { ZOHO_CONNECTION_KEY } from "../../domain/constants";
import type { ZohoConnectionSummary } from "../../domain/constants";
import { useSubStepNav } from "../useSubStepNav";
import { ADMIN_USER_EXPLAINER_KEY } from "./AddZournalStep";

/**
 * Two speeds, because two very different waits happen on this screen.
 *
 * Waiting for a client to send an invitation can run for hours, and polling
 * that every second and a half is a lot of requests to watch nothing happen.
 * The read-back is our own work, seven datasets at one per poll, and there a
 * slow tick is the only reason the screen looks slow.
 */
const POLL_WAITING_MS = 4000;
const POLL_WORKING_MS = 1500;

/**
 * Watch the connection come up, and read the books the moment it does.
 *
 * The waiting lives here rather than on Add Zournal because it is a different
 * kind of screen. Add Zournal asks for three fields to be copied into Zoho and
 * is done the moment they are; this one runs from "has the invitation arrived"
 * through accepting it, proving the access, and pulling the organisation's GST
 * registrations, banks, TDS and TCS rates, currencies and ledgers into our own
 * tables. Putting that on the copy screen made a step that should take seconds
 * look like one you have to sit through.
 *
 * Nothing here can be answered by the CA. Continue opens when the API has
 * confirmed we are inside the client's organisation AND we have read it, which
 * is the entire point of doing this server-side rather than taking "yes I added
 * you" at face value.
 */
export function ConfirmConnectionStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "confirm-connection");
  const invited = useCoAnswer<boolean>(clientId, ADMIN_USER_EXPLAINER_KEY);
  const recorded = useCoAnswer<ZohoConnectionSummary>(clientId, ZOHO_CONNECTION_KEY);

  const [state, setState] = useState<ZohoConnectStateDTO | undefined>();
  // Bumped by "Try again" to restart a poll loop that stopped on a parked seat.
  const [pollNonce, setPollNonce] = useState(0);

  const steps = state?.steps ?? [];
  const anyFailed = steps.some((step) => step.status === "failed");
  // Done means the whole sequence, not just the connection. The steps after it
  // are the read the review screens depend on, and letting Continue open before
  // they finish would hand the CA a set of empty review screens.
  const complete = steps.length > 0 && steps.every((step) => step.status === "done");

  async function handleRetry() {
    try {
      // Which retry depends on which half broke. Re-entering the connection
      // state machine would not fix a Zoho endpoint that failed to answer, and
      // re-reading the books cannot help a seat that never got accepted.
      const readbackBroke = steps.some(
        (step) => step.status === "failed" && step.key.startsWith("reading-")
      );

      setState(readbackBroke ? await retryZohoReadback(clientId) : await retryZohoConnect(clientId));
      setPollNonce((n) => n + 1);
    } catch {
      // The seat and the read-back each keep their own state; the next poll
      // reports the truth either way.
    }
  }

  useEffect(() => {
    if (!invited) return;

    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async (first: boolean) => {
      let next: ZohoConnectStateDTO | undefined;
      try {
        // start() is called again on every mount on purpose. It only moves a
        // seat still sitting at seat_claimed, so it is free when Add Zournal
        // already fired it, and it covers the case where that call failed.
        next = first ? await startZohoConnect(clientId) : await advanceZohoConnect(clientId);
        if (stopped) return;
        setState(next);

        // Stop once it settles: a finished sequence has nothing left to do, and
        // a failed step needs a retry rather than another identical attempt.
        const done = next.steps.every((step) => step.status === "done");
        const failed = next.steps.some((step) => step.status === "failed");
        if (done || failed) return;
      } catch {
        // A failed poll is not a failed connection — the server keeps its own
        // state — so keep trying rather than showing an error.
        if (stopped) return;
      }

      // Fast while we are the ones doing the work, slow while we are waiting on
      // somebody else's inbox.
      const working = next?.connection.connected ?? false;
      timer = setTimeout(() => void tick(false), working ? POLL_WORKING_MS : POLL_WAITING_MS);
    };

    void tick(true);

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [clientId, invited, pollNonce]);

  // Someone who never went through Add Zournal still gets to see where a
  // finished connection stands, without starting one.
  useEffect(() => {
    if (invited) return;
    let cancelled = false;
    zohoConnectStatus(clientId)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clientId, invited]);

  // Record the connection the moment it lands, not when Continue is pressed.
  // The workspace preview renders on every screen and has to be able to say
  // "connected" without the poll loop that proved it still being mounted, so
  // the fact is written into the client rather than kept in this component.
  // Written twice at most: once the moment the connection lands, so the preview
  // stops saying "not yet connected" without waiting for seven more reads, and
  // once when the reading is done and the counts are final. Rebuilding it from
  // the counts mid-read would be a write on every poll for a number that is
  // about to change again.
  const summary = useMemo<ZohoConnectionSummary | null>(() => {
    if (!state?.connection.connected) return null;

    const base = {
      organizationId: state.connection.zoho_organization_id,
      organizationName: state.connection.organization_name,
    };

    // Mid-read on a revisit, keep the counts already recorded for this org. A
    // finished read goes incomplete again while new contacts are swept
    // (ZOUR-276), and writing `base` over a fuller summary would leave the
    // preview saying 0 GSTINs and 0 banks if the CA left before it finished.
    if (!state.readback.complete) {
      if (!recorded || recorded.organizationId !== base.organizationId) return base;

      return {
        ...base,
        gstin: recorded.gstin,
        gstinCount: recorded.gstinCount,
        bankAccountCount: recorded.bankAccountCount,
      };
    }

    return {
      ...base,
      gstin: state.readback.organisation?.gstin ?? null,
      gstinCount: state.readback.counts.gstins,
      bankAccountCount: state.readback.counts.bank_accounts,
    };
  }, [state, recorded]);

  useEffect(() => {
    if (summary === null) return;
    // Field by field rather than by identity: the summary object is rebuilt on
    // every poll, and comparing serialised forms would make the answer depend
    // on key order surviving a round trip through the database.
    const same =
      recorded?.organizationId === summary.organizationId &&
      recorded?.organizationName === summary.organizationName &&
      (recorded?.gstinCount ?? null) === (summary.gstinCount ?? null) &&
      (recorded?.bankAccountCount ?? null) === (summary.bankAccountCount ?? null);
    if (same) return;

    void persistAnswer<ZohoConnectionSummary>(clientId, ZOHO_CONNECTION_KEY, summary);
  }, [clientId, recorded, summary]);

  if (!client) return null;

  const name = client.name || "this client";
  const warnings = complete
    ? [
        ...warningsForPlan(state?.connection.plan_name ?? null, client.zohoSizing?.chosenPlan),
        ...warningsForBooksHistory(
          client.bookkeepingType,
          state?.readback.counts.invoices ?? null,
          state?.readback.counts.bills ?? null
        ),
      ]
    : [];

  return (
    <SubStepShell
      title="Confirm Connection to Zoho"
      description={
        complete
          ? `Zournal is connected to ${name}'s Zoho Books and has read their setup.`
          : `Reading ${name}'s setup. This runs on its own and can take a few minutes. You can leave this page and come back.`
      }
      thenWhat={complete ? "Continue → verifying the setup." : "Continue opens once we're in and have read the books."}
      onContinue={() => void goNext()}
      // Gated on the API having confirmed the connection and the read having
      // finished, not on anyone saying they added us. Checking is the whole
      // reason this step exists.
      continueDisabled={!complete}
    >
      {invited ? (
        state ? (
          <>
            <ConnectionSequence
              title={`Connecting to ${name}'s books`}
              steps={state.steps}
              onRetry={anyFailed ? () => void handleRetry() : undefined}
            />
            {complete && <ReadbackSummary state={state} />}
            {warnings.map((warning) => (
              <Callout key={warning} variant="warning">
                {warning}
              </Callout>
            ))}
          </>
        ) : (
          <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
            Checking where the connection has got to…
          </div>
        )
      ) : (
        // Nothing to watch yet, and no reason to start the clock: the deadline
        // that parks a connection measures from the moment we begin hunting for
        // an invitation, so beginning before one was sent would park it for a
        // reason that was never the client's fault.
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed px-5 py-4">
          <p className="text-sm text-muted-foreground">
            Nothing to connect yet. Add Zournal as an admin user in the client's Zoho Books first.
          </p>
          <Link
            to={`${CO_BASE}/${clientId}/add-zournal`}
            className="text-sm font-medium underline underline-offset-2"
          >
            Back to Connect Zournal to Zoho Books
          </Link>
        </div>
      )}
    </SubStepShell>
  );
}

/**
 * What the connected organisation's plan is worth saying out loud.
 *
 * A Free plan never reaches here — the backend fails the seat before it
 * connects. What can arrive is a trial (works today, downgrades at expiry,
 * after onboarding is done and nobody is watching) and a plan that is not the
 * one the client was sized for. Both are warnings rather than blocks: the CA
 * may know the client is subscribing tomorrow, and this screen is the cheap
 * moment to notice, not the place to enforce a purchase.
 */
function warningsForPlan(planName: string | null, chosenPlan: string | undefined): string[] {
  if (!planName) return [];

  const warnings: string[] = [];
  const lower = planName.toLowerCase();

  if (lower.includes("trial")) {
    // The second sentence is Zoho's own rule, met live on 2026-08-17: creating
    // a bank account with a non-base currency_id on a trial org came back
    // refused (code 122018, "…upgrade to a paid plan…"). The banks step will
    // show that refusal readably, but this screen is the cheaper place to
    // learn it — before the CA has typed the account in.
    warnings.push(
      `This organisation is on a trial (${planName}). The connection keeps this access only until the trial ` +
        `expires. Make sure the client subscribes before go-live. Until then Zoho also refuses bank or ` +
        `credit-card accounts in any currency other than the base one — foreign-currency accounts wait for ` +
        `the subscription.`
    );
  }

  if (chosenPlan) {
    // "PREMIUM TRIAL" is the premium plan on trial, so the trial word comes
    // off before comparing against the sizing answer's plan id.
    const normalized = lower.replace("trial", "").trim();
    if (normalized !== chosenPlan.toLowerCase()) {
      const chosenLabel = chosenPlan.charAt(0).toUpperCase() + chosenPlan.slice(1);
      warnings.push(
        `This client was sized for the ${chosenLabel} plan on Select the Correct Zoho Books Subscription Package, but the connected organisation ` +
          `is on ${planName}. If that is not intentional, fix the plan in Zoho or revisit the sizing.`
      );
    }
  }

  return warnings;
}

/**
 * The document counts, held against what the CA said on Books questions.
 *
 * The counts cost one API call each and answer the question this screen is
 * for: are these the books we were told about? A "new" organisation that
 * already holds invoices is almost always the wrong organisation connected —
 * and this is the last screen where noticing that is cheap. Warnings, not
 * blocks: the CA can know things we don't (a test invoice, a migration
 * half-done), and the counts are on the card either way.
 */
function warningsForBooksHistory(
  bookkeepingType: string | undefined,
  invoices: number | null,
  bills: number | null
): string[] {
  if (invoices === null || bills === null) return [];

  const total = invoices + bills;
  const held = `${invoices} invoice${invoices === 1 ? "" : "s"} and ${bills} bill${bills === 1 ? "" : "s"}`;

  if ((bookkeepingType === "new-company" || bookkeepingType === "new-zoho-opening-balances") && total > 0) {
    return [
      `Connect Your Accounting Software says this client starts with a new organisation, but the connected one already holds ` +
        `${held}. Check the right organisation was connected, or revisit Connect Your Accounting Software.`,
    ];
  }

  if (bookkeepingType === "existing-zoho-current" && total === 0) {
    return [
      `Connect Your Accounting Software says this client's books already run in Zoho, but the connected organisation holds no ` +
        `invoices or bills. It looks freshly created. Check the right organisation was connected.`,
    ];
  }

  return [];
}

/** A fact and its value, for the two grids below. */
function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <span className="text-[13px] text-muted-foreground">
      {label} <span className={mono ? "font-mono text-foreground" : "font-medium text-foreground"}>{value}</span>
    </span>
  );
}

/**
 * What we found, once the reading is done.
 *
 * Two pieces, deliberately weighted differently. The organisation is reference
 * — a plain bordered box, the same as every other read-only card in the flow.
 * What we found in the books is a Callout, because it is the part worth
 * reading: a wrong GSTIN or a set of expired TDS rates is the whole reason for
 * putting a summary here rather than sending the CA straight on.
 *
 * The organisation is named with what Zoho reported rather than what we were
 * told. They are usually the same; when they are not, the one that proves we
 * joined the right books is Zoho's, and this is the last screen where noticing
 * that is cheap. GSTINs and bank accounts stay listed because their values are
 * what identify the organisation; the TDS & TCS rates are a count with an
 * expired flag, because their long section names drowned the card and the full
 * list has its own Verify setup screen.
 */
function ReadbackSummary({ state }: { state: ZohoConnectStateDTO }) {
  const { connection, readback } = state;
  const org = readback.organisation;
  const { counts, highlights } = readback;

  const taxRates = counts.tds_rates + counts.tcs_rates;
  const expired = highlights.tds_expired + highlights.tcs_expired;

  // A client who is not registered has no GSTINs to find, and saying so is a
  // different sentence from "none found", which reads like something went wrong.
  const gstins =
    counts.gstins > 0
      ? highlights.gstins.join(" · ")
      : org && !dbTrue(org.is_gst_registered)
        ? "Not GST registered in Zoho."
        : "None found. We'll confirm this in Verify setup.";

  const banks =
    counts.bank_accounts > 0
      ? highlights.bank_accounts.join(" · ")
      : "None found. We'll confirm this in Verify setup.";

  // The expired flag stays even in the short form: a rate count looks
  // perfectly healthy until you notice none of it is in date.
  const rates =
    taxRates === 0
      ? "None set up. We'll confirm this in Verify setup."
      : expired === 0
        ? `${taxRates}`
        : `${taxRates} (${expired === taxRates ? "all" : expired} expired)`;

  return (
    <>
      <div className="grid gap-x-6 gap-y-1.5 rounded-lg border px-5 py-4 sm:grid-cols-2">
        <Fact label="Organisation" value={org?.name || connection.organization_name || "—"} />
        <Fact label="ID" value={connection.zoho_organization_id ?? "—"} mono />
        {connection.user_role && <Fact label="Our access" value={connection.user_role} />}
        {connection.plan_name && <Fact label="Zoho plan" value={connection.plan_name} />}
        {org?.base_currency && <Fact label="Base currency" value={org.base_currency} />}
        {org?.fiscal_year_start && <Fact label="Financial year starts" value={org.fiscal_year_start} />}
        {org?.state && <Fact label="State" value={org.state} />}
        {org?.tax_basis && <Fact label="Tax basis" value={org.tax_basis} />}
      </div>

      <Callout>
        <div className="flex flex-col gap-1">
          <SummaryRow label={counts.gstins > 1 ? "GSTINs" : "GSTIN"} value={gstins} />
          <SummaryRow label="Bank accounts" value={banks} />
          <SummaryRow label="TDS & TCS rates" value={rates} />
          {counts.invoices !== null && counts.bills !== null && (
            <SummaryRow
              label="Documents"
              value={`${counts.invoices} invoice${counts.invoices === 1 ? "" : "s"} · ${counts.bills} bill${counts.bills === 1 ? "" : "s"}`}
            />
          )}
          <SummaryRow
            label="Also read"
            value={`${counts.ledgers} ledger${counts.ledgers === 1 ? "" : "s"} · ${counts.currencies} currenc${counts.currencies === 1 ? "y" : "ies"} · ${counts.tax_rates} GST rate${counts.tax_rates === 1 ? "" : "s"}`}
          />
        </div>
      </Callout>
    </>
  );
}

/** One short line of the read-back summary: a bold label, then the value. */
function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="font-medium text-foreground">{label}:</span> {value}
    </span>
  );
}
